import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageShell, SectionLabel, SlideCorner } from "@/components/Layout";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";

export default function CreativeClass() {
  const [honorees, setHonorees] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", discipline: "music", city: "Mumbai", statement: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { api.get("/content/creative-class").then(({ data }) => setHonorees(data.honorees)); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.statement) { toast.error("Name, email and the reason are required"); return; }
    setSubmitting(true);
    try {
      await api.post("/applications", { ...form, program: "creative_class", course: "" });
      toast.success("Nomination received. Jury reviews quarterly.");
      setForm({ ...form, name: "", email: "", statement: "" });
    } catch { toast.error("Failed"); }
    finally { setSubmitting(false); }
  };

  return (
    <PageShell>
      <section className="relative grid-bg border-b border-border">
        <SlideCorner right="THE CREATIVE CLASS" />
        <div className="max-w-[1600px] mx-auto px-5 md:px-10 pt-20 pb-16">
          <SectionLabel>The Creative Class · India's 30 Under 30</SectionLabel>
          <h1 className="font-display text-5xl md:text-7xl lg:text-[7rem] font-bold leading-[0.9] tracking-tighter">
            The credential that goes in a bio for <span className="hl-cream">a decade.</span>
          </h1>
          <p className="mt-10 max-w-3xl text-white/80 text-base md:text-lg leading-relaxed">
            30 honorees. 7 disciplines. One national press moment. Jury convened from across India's most influential cultural institutions. Authority unbuyable — only earned.
          </p>
        </div>
      </section>

      <section className="border-b border-border">
        <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-16">
          <SectionLabel>Edition 01 · Spotlight</SectionLabel>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-border">
            {honorees.map((h, i) => (
              <div key={h.name} className="bg-background p-5">
                <div className="aspect-[3/4] bg-muted overflow-hidden mb-4 relative">
                  {h.image && <img src={h.image} alt={h.name} className="w-full h-full object-cover" />}
                  <span className="absolute top-3 left-3 meta-mono !text-white/85 bg-black/50 px-2 py-1">№ {String(i + 1).padStart(2, "0")}</span>
                </div>
                <p className="meta-mono !text-primary">{h.discipline.replace("_", " ")} · {h.city}</p>
                <p className="font-display text-xl font-bold mt-1">{h.name}</p>
                <p className="font-serif-italic text-white/70 text-xs mt-2">{h.tagline}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section>
        <div className="max-w-[1000px] mx-auto px-5 md:px-10 py-20">
          <SectionLabel>Nominate · Edition 02 opens Q3 2026</SectionLabel>
          <h2 className="font-display text-4xl md:text-6xl font-bold leading-tight mb-10">Who deserves to be <span className="hl-coral">named</span>?</h2>
          <form onSubmit={submit} className="grid md:grid-cols-2 gap-px bg-border">
            <F label="Nominee name" v={form.name} on={(v) => setForm({ ...form, name: v })} testid="cc-name" />
            <F label="Your email" type="email" v={form.email} on={(v) => setForm({ ...form, email: v })} testid="cc-email" />
            <F label="Discipline" v={form.discipline} on={(v) => setForm({ ...form, discipline: v })} testid="cc-discipline" />
            <F label="City" v={form.city} on={(v) => setForm({ ...form, city: v })} testid="cc-city" />
            <div className="md:col-span-2 bg-background p-6">
              <p className="meta-mono mb-3">— WHY (200 words)</p>
              <textarea data-testid="cc-statement" rows={5} value={form.statement} onChange={(e) => setForm({ ...form, statement: e.target.value })} className="w-full bg-transparent outline-none text-base font-light border-b border-border focus:border-primary py-3" />
            </div>
            <div className="md:col-span-2 bg-background p-6 flex justify-end">
              <button data-testid="cc-submit" disabled={submitting} className="btn-pill btn-coral">{submitting ? "Sending…" : <>Nominate <ArrowRight size={14} /></>}</button>
            </div>
          </form>
        </div>
      </section>
    </PageShell>
  );
}
function F({ label, v, on, type = "text", testid }) {
  return (
    <div className="bg-background p-6">
      <p className="meta-mono mb-3">— {label.toUpperCase()}</p>
      <input data-testid={testid} type={type} value={v} onChange={(e) => on(e.target.value)} className="w-full bg-transparent outline-none text-lg font-light border-b border-border focus:border-primary py-2" />
    </div>
  );
}
